import React from 'react';
import { Plus } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

export default function ProductCard({ product }) {
  const { addItem } = useCart();

  const getStockStatus = () => {
    if (!product.is_gourmet) return null;
    if (product.stock === 0) return { color: 'red', text: 'Agotado' };
    if (product.stock <= 5) return { color: 'amber', text: `Solo ${product.stock} disponibles` };
    return { color: 'green', text: 'Disponible' };
  };

  const stockStatus = getStockStatus();
  const isOutOfStock = product.is_gourmet && product.stock === 0;

  const getCategoryEmoji = (category) => {
    const emojis = {
      'espacio-fresco': '🥗',
      'frutas': '🍎',
      'verduras': '🥦',
      'pan': '🥖',
      'congelados': '❄️',
      'gourmet': '🦀'
    };
    return emojis[category] || '🛒';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'espacio-fresco': 'Espacio Fresco',
      'frutas': 'Frutas',
      'verduras': 'Verduras',
      'pan': 'Panadería',
      'congelados': 'Congelados',
      'gourmet': 'Gourmet'
    };
    return labels[category] || category;
  };

  return (
    <div 
      className={`product-card bg-white border border-[#E8EAE6] rounded-xl overflow-hidden ${isOutOfStock ? 'opacity-60' : ''}`}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] bg-[#F3F3EF] overflow-hidden">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="product-image w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl">
            {getCategoryEmoji(product.category)}
          </div>
        )}
        
        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            product.is_gourmet 
              ? 'bg-[#D96C4A] text-white' 
              : 'bg-white/90 text-[#2C5530]'
          }`}>
            {product.is_gourmet ? '★ Gourmet' : getCategoryLabel(product.category)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-cabinet font-semibold text-[#1A231C] mb-1 line-clamp-1">{product.name}</h3>
        <p className="text-sm text-[#808E83] line-clamp-2 mb-3 h-10">{product.description}</p>
        
        {/* Stock Status for Gourmet */}
        {stockStatus && (
          <div className="flex items-center gap-2 mb-3">
            <span className={`stock-dot ${stockStatus.color}`}></span>
            <span className="text-xs text-[#4A5D4E]">{stockStatus.text}</span>
          </div>
        )}

        {/* Price & Add Button */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-[#808E83]">{product.unit}</span>
            <div className="font-cabinet font-bold text-lg text-[#2C5530]">
              ${product.price.toLocaleString('es-CL')}
            </div>
          </div>
          
          <button
            onClick={() => !isOutOfStock && addItem(product)}
            disabled={isOutOfStock}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              isOutOfStock 
                ? 'bg-gray-200 cursor-not-allowed' 
                : 'bg-[#2C5530] hover:bg-[#3A6E40] text-white'
            }`}
            data-testid={`add-to-cart-${product.id}`}
          >
            <Plus size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
